import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { MenuItem, Select } from '@material-ui/core'
import {
    defaultSuggestionCardFilter,
    useSuggestionsShallowSelector,
} from 'components/WorkspaceEditor/SuggestionStore'
import { TrackChangeStatus } from 'models/changes'
import { TrackChangeStatusLabels } from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceRightPane/enums/TrackChangeStatusLabels'
import React, { useCallback } from 'react'
import { useTrackChangesFilterStyles } from '../../styles'
import { useTrackChangesFilter } from './hooks/useTrackChangesFilter'

/**
 * TrackChangesFilter component renders a filter dropdown for track changes.
 * It allows users to filter changes based on their resolution type.
 *
 * @returns {JSX.Element} - The rendered filter dropdown component.
 */
export const TrackChangesFilter = React.memo(() => {
    const classes = useTrackChangesFilterStyles()
    const { suggestionCardFilter } = useSuggestionsShallowSelector((state) => ({
        suggestionCardFilter: state.suggestionCardFilter,
    }))
    const { handleFilterResolutionTypeChange } = useTrackChangesFilter()

    const menuProps = React.useMemo(
        () => ({
            getContentAnchorEl: null,
            anchorOrigin: {
                vertical: 'bottom' as 'bottom',
                horizontal: 'left' as 'left',
            },
            transformOrigin: {
                vertical: 'top' as 'top',
                horizontal: 'left' as 'left',
            },
            PaperProps: {
                'aria-label': 'Resolution Type Filter Options',
            },
        }),
        []
    )

    const getTrackChangeOptions = useCallback((): React.ReactElement[] => {
        return Object.values(TrackChangeStatus)
            .filter((status) => status !== TrackChangeStatus.OPTIONGROUP)
            .map((status) => (
                <MenuItem
                    className={classes.filterOptionItem}
                    key={status}
                    value={status}
                >
                    {
                        TrackChangeStatusLabels[
                            status as keyof typeof TrackChangeStatus
                        ]
                    }
                </MenuItem>
            ))
    }, [classes.filterOptionItem])

    const options = React.useMemo(() => getTrackChangeOptions(), [classes])

    return (
        <GridItem
            className={classes.filterContainer}
            data-testid="filterGridItem"
        >
            <Select
                data-testid="filterDropdownTrackChanges"
                value={suggestionCardFilter}
                onChange={handleFilterResolutionTypeChange}
                variant="outlined"
                className={classes.selectInput}
                classes={{
                    outlined: classes.selectSelectOutlined,
                }}
                aria-label="Resolution Type Filter"
                title="Resolution Type Filter"
                MenuProps={menuProps}
            >
                <MenuItem
                    className={classes.filterOptionItem}
                    value={defaultSuggestionCardFilter}
                >
                    {defaultSuggestionCardFilter}
                </MenuItem>
                {options}
            </Select>
        </GridItem>
    )
})
